import { request, requestBlob } from './api';
import type { DicomSeriesAnalysis, DicomStudyAnalysis } from '../types/dicom';

export interface PatientRecord {
  id: string;
  doctor_id: string;
  upload_id: string;
  patient_name: string;
  birth_date: string;
  gender: string;
  study_instance_uid: string;
  fixed_series_instance_uid: string;
  has_photo?: boolean;
  dicom_studies: DicomStudyAnalysis[];
  dicom_series: DicomSeriesAnalysis[];
  created_at: string;
  updated_at: string;
}

export type PatientCreateInput = Omit<
  PatientRecord,
  'id' | 'doctor_id' | 'has_photo' | 'created_at' | 'updated_at'
>;

interface PatientListResponse {
  patients: PatientRecord[];
  total: number;
}

export function listPatients(token: string) {
  return request<PatientListResponse>('/patients', { token });
}

export type PatientUpdateInput = Pick<PatientRecord, 'patient_name' | 'birth_date' | 'gender'>;

export function getPatient(patientId: string, token: string, signal?: AbortSignal) {
  return request<PatientRecord>(`/patients/${patientId}`, { token, signal });
}

export function updatePatient(patientId: string, payload: PatientUpdateInput, token: string) {
  return request<PatientRecord>(`/patients/${patientId}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
    token,
  });
}

export function createPatient(payload: PatientCreateInput, token: string) {
  return request<PatientRecord>('/patients', {
    method: 'POST',
    body: JSON.stringify(payload),
    token,
  });
}

export function uploadPatientPhoto(uploadId: string, photo: File, token: string) {
  const body = new FormData();
  body.append('photo', photo);
  return request<{ has_photo: boolean }>(`/patients/uploads/${uploadId}/photo`, {
    method: 'POST',
    body,
    token,
  });
}

export function updatePatientPhoto(patientId: string, photo: File, token: string, demographics?: PatientUpdateInput) {
  const body = new FormData();
  body.append('photo', photo);
  if (demographics) body.append('demographics', JSON.stringify(demographics));
  return request<PatientRecord>(`/patients/${patientId}/photo`, { method: 'PUT', body, token });
}

export function getPatientPhoto(patientId: string, token: string, signal?: AbortSignal, version?: string) {
  const query = version ? `?v=${encodeURIComponent(version)}` : '';
  return requestBlob(`/patients/${patientId}/photo${query}`, { token, signal });
}

export function deletePatient(patientId: string, token: string) {
  return request<void>(`/patients/${patientId}`, {
    method: 'DELETE',
    token,
  });
}
